/**
 * Issue #608 — LOADING THE GROUNDS DIRECTORY, AND THE ONE RULE ABOUT FAILURE.
 *
 * ## What this is for
 *
 * `GET /api/grounds` started returning 500 for the pilot's only admin: the
 * endpoint bound one D1 parameter per visible ground, D1 caps a query at 100,
 * and he crossed it at his 101st. That was an API bug and is fixed in
 * `api/src/db/repos/chunk.ts`. THIS module exists because of what the page did
 * with the failure.
 *
 * `loadGrounds` had no `.catch()`. The rejected promise left `grounds` at `[]`,
 * and the page rendered its ordinary empty state — "No grounds here yet". So a
 * working account with 101 venues was told, in the product's own words, that it
 * had none. He believed his data had been deleted, and there was nothing on the
 * screen to suggest otherwise.
 *
 * ## The rule
 *
 * **An empty list may only ever mean empty.** Every other outcome — a 500, a
 * timeout, a dropped connection — is a FAILURE and must be shown as one, with a
 * way to try again. `directoryOutcome` is that rule written down once, and it
 * checks `failed` BEFORE it looks at any count, because a failed fetch and an
 * empty directory produce an identical list and the list cannot tell them
 * apart.
 *
 * ## Why it lives here and not in the page
 *
 * `GroundsMap.jsx` mounts leaflet, so this repo does not render it in unit
 * tests (see `GroundsMap.test.js`'s header). A rule that only exists inside
 * that component can only ever be tested by grepping its source. Pulled out
 * here, "a rejected fetch is not an empty directory" is an ordinary function
 * with an ordinary test that actually rejects a promise and asserts what comes
 * back — which is the test that would have caught this.
 */
import { readViewportFacilities, readViewportGrounds, viewportQuery } from "./mapViewport";
import { readDirectorySearch, searchQuery } from "./directorySearch";

/**
 * Issue #683 — THE ARCHIVED OPT-IN IS ADMIN-ONLY.
 *
 * `include_archived=1` used to be a bare module constant: every caller of
 * this file got it, unconditionally, because the map WAS the only surface
 * that could see or restore an archived venue. `/admin/venues` (#603) made
 * that false — it is now the record keeper's own surface — and left the map
 * showing every player and coach a state they have no affordance for.
 *
 * So the base path is no longer a constant; it is a function of WHO is
 * asking. An admin still gets the opt-in, unchanged. A player or coach gets
 * the API's own default, which already excludes archived rows at the query
 * — this is dropping an opt-in a non-admin never needed, not adding a new
 * filter.
 *
 * This is deliberately NOT the whole story for a player: `GroundsMap.jsx`
 * folds a saved or played archived venue back in from its own personal
 * reads (`/players/me/grounds`, `/venues/played`), which keep archived rows
 * for the reason `lib/archived.js` states. The principle that makes that
 * fold-in consistent with dropping every OTHER archived row, rather than a
 * contradiction of it, is written where that folding happens.
 */
export function directoryBasePath(isAdmin) {
  return isAdmin ? "/grounds?include_archived=1" : "/grounds";
}

/** The surfaces layer that draws on top of it (#166 FAC-06) — same rule, same reason. */
export function facilitiesBasePath(isAdmin) {
  return isAdmin ? "/grounds/facilities?include_archived=1" : "/grounds/facilities";
}

/**
 * Issue #643 — THE SAME TWO READS, BOUNDED BY THE VIEWPORT.
 *
 * Both paths gain `&bbox=…&zoom=…` when there is a viewport to send, and both
 * carry the SAME role-conditioned base path: the box is orthogonal to
 * visibility. "Which rows may this caller see" is `isAdmin`'s question; only
 * "which of them are on screen" is the viewport's.
 *
 * The viewport is OPTIONAL, and that is a shape rather than laziness.
 * `loadDirectoryGrounds(api)` with no box is still the unbounded read every
 * other caller of this endpoint makes and is what these functions did before
 * this issue. What epic #609 requires is that THE MAP never asks without a
 * box — which is asserted where the map wires it up (`GroundsMap.test.js`),
 * in `useDirectoryViewport`, and end-to-end in journey 26.
 */
export function directoryPath(viewport, isAdmin) {
  return withViewport(directoryBasePath(isAdmin), viewport);
}

export function facilitiesPath(viewport, isAdmin) {
  return withViewport(facilitiesBasePath(isAdmin), viewport);
}

/**
 * Issue #644 — THE SEARCH READ, AND THE VIEWPORT IT DOES NOT CARRY.
 *
 * Same base path — role-conditioned, #683 — and deliberately no `bbox` and
 * no `zoom` (`searchQuery` in `lib/directorySearch.js` carries the
 * argument). A search is a question about the DIRECTORY; bounding it by the
 * frame the last pan happened to leave the map in would make it fail
 * precisely when it is needed, which is what #643's world-box bridge
 * existed to avoid and what that bridge could no longer do past
 * `GROUNDS_BBOX_MAX_ROWS`.
 *
 * The base path carrying the role check here, and only here, is what keeps
 * #683's acceptance criterion 5 true: search obeys the same rule as the
 * viewport read because both ask this one function for the same base path.
 */
export function searchPath(term, page, isAdmin) {
  const base = directoryBasePath(isAdmin);
  const query = searchQuery(term, page);
  return query ? appendQuery(base, query) : base;
}

function withViewport(path, viewport) {
  const query = viewportQuery(viewport);
  return query ? appendQuery(path, query) : path;
}

/**
 * Issue #683 — the base path is no longer guaranteed to already carry a `?`.
 * Before this issue every base path was a constant ending in
 * `?include_archived=1`, so `${path}&${query}` was always safe; a non-admin
 * base path is now the bare `/grounds`/`/grounds/facilities`, with no query
 * string of its own, and `&` there would have produced `/grounds&bbox=…` —
 * a malformed URL a mocked test doesn't reject but a real fetch would. This
 * is the one join point both `withViewport` and `searchPath` go through, so
 * there is exactly one place that gets `?` vs `&` right.
 */
function appendQuery(path, query) {
  return `${path}${path.includes("?") ? "&" : "?"}${query}`;
}

/**
 * The words a failure uses. Held here so the map notice and the list below
 * cannot drift into telling the user two different stories about one request,
 * and so a test can assert the page never describes a failure as an emptiness.
 */
export const DIRECTORY_ERROR_TITLE = "We couldn't load the directory";
export const DIRECTORY_ERROR_BODY =
  "Something went wrong fetching your grounds — this is a problem at our end, not missing data. Nothing has been deleted.";
export const DIRECTORY_RETRY_LABEL = "Retry";

/**
 * Fetch the directory. Never throws, and NEVER reports an empty list for a
 * failed request: on rejection the result carries no `grounds` at all, so a
 * caller physically cannot mistake the failure for a directory of zero.
 */
export async function loadDirectoryGrounds(api, viewport, isAdmin) {
  try {
    const r = await api.get(directoryPath(viewport, isAdmin));
    // #643: one normalised shape for both contracts — rows and cells — read
    // off the response's own `mode` rather than guessed from which key is
    // present. `readViewportGrounds` still accepts the bare array the
    // unbounded read returns, so dropping the box would degrade to the old
    // behaviour rather than to a blank page.
    return { ok: true, ...readViewportGrounds(r?.data) };
  } catch {
    return { ok: false };
  }
}

/**
 * Fetch one page of a search. Never throws, and — #608/#633's rule, which
 * this path inherits rather than restates — NEVER reports an empty list for a
 * failed request: on rejection the result carries no `grounds` at all, so a
 * caller physically cannot render "No matches" over a 500. A search that
 * fails must say it failed; "nothing matches" is a claim about the directory.
 */
export async function loadDirectorySearch(api, term, page, isAdmin) {
  try {
    const r = await api.get(searchPath(term, page, isAdmin));
    return { ok: true, ...readDirectorySearch(r?.data) };
  } catch {
    return { ok: false };
  }
}

/**
 * PR #921 review (F1) — resolves ONE ground by id WITHOUT the unbounded
 * `GET /grounds` read's own cap. That read orders by id and stops at
 * `GROUNDS_BBOX_MAX_ROWS` (api/src/db/repos/grounds.ts) — 1,000 rows
 * against a ~20k-row directory — so a ground created after the 1,000th
 * simply never appears in it, and every `.find(g => g.ground_id === id)`
 * against that read was silently answering "not found" for it. Both
 * `GroundCard.jsx` (opened from a played/nearby row) and
 * `LogMatchGroundStep.jsx` (opened from GroundCard's own "Log a match
 * here", carrying `?ground=`) hit exactly this.
 *
 * Two escapes, neither an API change:
 *
 *   1. A TIGHT bbox around a known coordinate. Row mode is never capped
 *      by directory SIZE, only by how many visible grounds sit inside the
 *      snapped 0.25° grid cell the box lands in (`VIEWPORT_GRID_DEGREES`,
 *      api/src/lib/viewport.ts) — a single ground's own neighbourhood, not
 *      the whole table's id order. `viewportQuery` (`lib/mapViewport.js`)
 *      always appends `zoom` once a box is given — omitting it would send
 *      the literal string `zoom=undefined` — so a fixed, high `zoom: 15`
 *      is supplied for that reason alone; it does NOT keep the server out
 *      of cells mode. `zoom`'s PRESENCE is what lets the server answer
 *      cells at all (`grounds.ts`'s own gate: no zoom, no aggregation,
 *      ever) — a box dense enough (>1,000 visible grounds in that one
 *      grid cell) still comes back as `mode: "cells"`, which is exactly
 *      the case the `byBox.mode !== "cells"` check below refuses, falling
 *      through to the search fallback instead of trusting a cell answer
 *      that carries no ground rows at all.
 *   2. Failing that (no coordinate at all, the box came back empty, or it
 *      aggregated into cells), a name SEARCH (#644) — paged by MATCH
 *      RELEVANCE, not id order, so a specific id is never pushed off the
 *      page by however many grounds happen to sit earlier in the table.
 *
 * Both paths carry the caller's own visibility rule
 * (`visibleGroundsWhere`) exactly as the uncapped read did — a ground
 * genuinely invisible to this caller still resolves to `null`, same as a
 * ground that does not exist; this function is not a new way to see
 * something the directory would otherwise hide.
 *
 * `hint` fields are all optional — a caller with only an id (no
 * coordinate, no name) gets `null` back rather than falling through to
 * the very unbounded read this function exists to avoid.
 */
export async function resolveGroundById(api, groundId, { latitude, longitude, name, isAdmin = false } = {}) {
  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    // ~1.1km on a side before the server snaps it outward to its own
    // grid — small enough that even a dense city block stays well under
    // the aggregate-row trigger for an ordinary seed/production spread.
    const delta = 0.01;
    const viewport = {
      box: {
        minLat: latitude - delta,
        minLng: longitude - delta,
        maxLat: latitude + delta,
        maxLng: longitude + delta,
      },
      // Required by `viewportQuery` once a box is given (it always
      // appends `zoom`) — NOT a guard against cells mode; sending it is
      // what lets the server answer cells at all if this box turns out
      // dense. The `mode !== "cells"` check below is the real guard.
      zoom: 15,
    };
    const byBox = await loadDirectoryGrounds(api, viewport, isAdmin);
    if (byBox.ok && byBox.mode !== "cells") {
      const row = (byBox.grounds || []).find((g) => g.ground_id === groundId);
      if (row) return row;
    }
  }
  if (name) {
    const bySearch = await loadDirectorySearch(api, name, 1, isAdmin);
    if (bySearch.ok) {
      const row = (bySearch.grounds || []).find((g) => g.ground_id === groundId);
      if (row) return row;
    }
  }
  return null;
}

/**
 * Fetch the surfaces layer. This one DOES degrade to an empty list on failure
 * and always has (#166: a directory that draws its grounds without their nets
 * is degraded but honest, where refusing to draw grounds because their nets
 * failed turns an extra into a dependency). What #608 adds is `ok: false`
 * alongside it, so the degradation can be admitted on screen instead of
 * looking like a venue that genuinely has no surfaces.
 */
export async function loadDirectoryFacilities(api, viewport, isAdmin) {
  try {
    const r = await api.get(facilitiesPath(viewport, isAdmin));
    // #643/#641: the bounded read answers in an envelope carrying its own
    // `truncated`/`row_cap`, so the page can say "showing the first 1,000
    // surfaces here" instead of drawing a partial layer as if it were whole.
    return { ok: true, ...readViewportFacilities(r?.data) };
  } catch {
    return { ok: false, facilities: [], truncated: false, cap: null };
  }
}

/**
 * What the page is looking at: `"failed"`, `"aggregated"`, `"aggregated-partial"`,
 * `"empty"`, or `"list"`.
 *
 * `failed` is tested first and wins unconditionally. That ordering IS the fix
 * — reverse it and #608's symptom comes straight back, because `count` is 0 in
 * both cases and the caller has no other evidence.
 *
 * ## `aggregated` is #643's addition, and it is the same rule again
 *
 * When a box holds more grounds than the row cap the server answers in
 * COUNTS PER CELL and sends no rows at all (#642/#643), so `count` is 0 —
 * for a third reason, with a third remedy. Left unnamed, a user at the map's opening zoom would be told
 * "No grounds here yet" while looking at bubbles counting twenty thousand of
 * them: #608's exact defect, produced by a different mechanism.
 *
 * It is tested AFTER `failed` for #608's reason (a failed fetch in cell mode
 * is still a failure, and the remedy is a retry) and BEFORE `empty` because
 * an aggregate answer is not an empty one.
 *
 * ## `aggregated-partial` is #688's addition
 *
 * #688 stopped `enrichDirectory` from blanking a player's saved/played
 * grounds while the map is aggregated — see `lib/directoryEnrich.js`'s
 * `foldSuppressed`. So `count` in cell mode is no longer always 0: it can be
 * a small personal list sitting in front of bubbles totalling thousands.
 * `"aggregated"` (below, `count === 0`) still means "nothing to show but the
 * bubbles"; `"aggregated-partial"` means "here is your own list, and the
 * bubbles are still the rest of the world" — two different sentences, so
 * they are two different outcomes rather than one branch that would have to
 * re-ask `count` itself.
 *
 * ## `pending` and `searching` are #644's two additions, and they are the
 * same rule a fourth and fifth time
 *
 * `pending` — a search has been typed and its answer has not arrived. Zero
 * rows, for a reason that is neither a failure nor an emptiness, and whose
 * remedy is to wait.
 *
 * `searching` — the list is the SEARCH's answer, so the viewport's `mode` is
 * not about it. A search typed while the map is zoomed out far enough to be
 * showing count bubbles would otherwise be reported as an aggregate, over a
 * list of results that is right there.
 */
export function directoryOutcome({ failed, count, mode, searching, pending }) {
  if (failed) return "failed";
  // #644 — A QUESTION WITH NO ANSWER YET IS NOT AN EMPTY ONE. The search runs
  // on the server now, so between the keystroke and the response there is a
  // moment with a term and no rows. Reporting "No grounds match" there would
  // be a WRONG answer rather than a slow one, over a directory that does
  // match — #608's defect produced by a fourth mechanism.
  if (pending) return "pending";
  // #644 — while a SEARCH is active the list is the search's answer, and the
  // viewport's mode has nothing to do with it. Without this, a search run
  // from a zoomed-out map (where the last viewport answer was counts) would
  // report "the map is showing how many are in each area" over a perfectly
  // good list of results.
  if (mode === "cells" && !searching) return count > 0 ? "aggregated-partial" : "aggregated";
  if (!count) return "empty";
  return "list";
}
